export const timezone = () => Temporal.Now.timeZoneId();

export const unixSeconds = () => Math.floor(Date.now() / 1000);

/**
 * Takes a local datetime and converts to UTC based on the user's time zone
 * `2025-10-15T10:00:00` (user in +01:00) -> `2025-10-15T09:00:00z`
 */
export const localToUtc = (localDatetime: string) => {
  return Temporal.PlainDateTime.from(localDatetime)
    .toZonedDateTime(timezone())
    .toInstant();
};

/**
 * Takes a UTC datetime and converts to zoned datetime based on the user's time zone
 * `2025-12-01T09:00:00Z` -> (user in LA) -> `2025-12-01T01:00:00-08:00[America/Los_Angeles]`
 */
export const utcToZoned = (utcDatetime: string) => {
  return Temporal.Instant.from(utcDatetime).toZonedDateTimeISO(timezone());
};

/**
 * Takes a UTC datetime and converts to local based on the user's time zone
 * `2025-12-01T09:00:00Z` -> (user in LA) -> `2025-12-01T01:00:00`
 */
export const utcToLocal = (utcDatetime: string) => {
  return utcToZoned(utcDatetime).toPlainDateTime();
};

/**
 * Takes a datetime with time zone and converts to local based on the user's time zone
 * `2025-12-01T01:00:00-08:00[America/Los_Angeles]` -> (user in London) -> `2025-12-01T09:00:00`
 */
export const zonedToLocal = (zonedDatetime: string) => {
  return Temporal.ZonedDateTime.from(zonedDatetime)
    .withTimeZone(timezone())
    .toPlainDate();
};

export const now = () => {
  // Temporal instants default to nanosecond precison
  return Temporal.Now.plainDateTimeISO().round({ smallestUnit: "minute" });
};

export const within = (date: string, thenOptions: Temporal.DurationLike) => {
  const now = utcToZoned(date);
  const then = Temporal.Now.zonedDateTimeISO().subtract(thenOptions);
  return Temporal.PlainDateTime.compare(now, then) >= 0;
};

export const today = () =>
  Temporal.Now.zonedDateTimeISO().withPlainTime("00:00");

/**
 * Takes a UTC datetime and returns whether it occured before today's calendar date
 * `2026-01-31T00:00:00.000Z` on Jan 1st returns `false`
 * `2026-01-31T00:00:00.000Z` on Feb 1st returns `true`
 */
export const beforeTodayUtc = (utcDateTime: Temporal.PlainDateTime) => {
  const now = Temporal.Now.plainDateISO("UTC");
  return Temporal.PlainDate.compare(utcDateTime, now) < 0;
};

type InstantInput = string | Temporal.Instant;

/**
 * Helper so functions can accept either a UTC string or a Temporal Instant
 * So call-sites don't have to create an instant to do comparisons
 */
const toInstant = (value: InstantInput) =>
  typeof value === "string" ? Temporal.Instant.from(value) : value;

/**
 * Check whether `date` is after `then`.
 * @example
 * ```ts
 * isAfter(assessment.createdDate, deadline)
 * ```
 */
export const isAfter = (date: InstantInput, then: InstantInput) =>
  Temporal.Instant.compare(toInstant(date), toInstant(then)) > 0;

export const beforeNow = (instant: InstantInput) =>
  isAfter(Temporal.Now.instant(), instant);

export const afterNow = (instant: InstantInput) =>
  isAfter(instant, Temporal.Now.instant());

/**
 * Comparator that orders instants oldest-first. Accepts UTC strings.
 * @example
 * ```ts
 * packages.toSorted((a, b) => sortAsc(a.deadline, b.deadline))
 * ```
 */
export const sortAsc = (a: InstantInput, b: InstantInput) =>
  Temporal.Instant.compare(toInstant(a), toInstant(b));

/**
 * Comparator that orders instants oldest-last. Accepts UTC strings.
 * @example
 * ```ts
 * packages.toSorted((a, b) => sortDesc(a.deadline, b.deadline))
 * ```
 */
export const sortDesc = (a: InstantInput, b: InstantInput) =>
  Temporal.Instant.compare(toInstant(b), toInstant(a));

export const days = (length: number) => {
  const date = Temporal.Now.plainDateISO();
  return Array.from({ length }, (_, i) => {
    const dayBefore = date.subtract({ days: i });
    return [dayBefore.toString(), { date: dayBefore, count: 0 }] satisfies [
      string,
      { date: Temporal.PlainDate; count: number },
    ];
  }).reverse();
};

export const formatWeekday = (date: Temporal.PlainDate) => {
  return date.toLocaleString(navigator.languages, {
    weekday: "short",
  });
};

export const monthRange = (
  startUtc: string,
  endUtc: string,
  interval: "month" | "year"
) => {
  const start = utcToZoned(startUtc)
    .toPlainDate()
    .with(interval === "year" ? { day: 1, month: 1 } : { day: 1 });
  const end = utcToZoned(endUtc).toPlainDate();
  const increment = interval === "month" ? { months: 1 } : { years: 1 };

  const result = [];
  let current = start;

  while (Temporal.PlainDate.compare(current, end) <= 0) {
    result.push(current);
    current = current.add(increment);
  }
  return result;
};

export const dateRange = (
  start: Temporal.ZonedDateTime,
  end: Temporal.ZonedDateTime,
  intervalMonths: number
) => {
  if (Temporal.PlainDate.compare(start, end) >= 0) return [];
  if (intervalMonths === 0) return [start];

  const result = [];
  for (
    let d = start;
    Temporal.PlainDate.compare(d, end) <= 0;
    d = d.add({ months: intervalMonths })
  ) {
    result.push(d);
  }
  return result;
};

export const getMonth = (date: Temporal.PlainDate) => {
  return date.toLocaleString(navigator.languages, { month: "long" });
};

const dmmyyyyOptions: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

const dmmyyyyTimeOptions: Intl.DateTimeFormatOptions = {
  ...dmmyyyyOptions,
  hour: "2-digit",
  minute: "2-digit",
};

export const dmmmyyyy = (date: Temporal.PlainDateTime | Temporal.PlainDate) => {
  return date.toLocaleString(navigator.languages, dmmyyyyOptions);
};

/** `Wednesday 14 October 2026` */
export const weekdayDate = (date: Temporal.PlainDate) =>
  date.toLocaleString(navigator.languages, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export const dmmm = (date: Temporal.PlainDate) =>
  date.toLocaleString(navigator.languages, { day: "numeric", month: "short" });

export const dmmmyyyyTime = (date: Temporal.PlainDateTime) => {
  return date.toLocaleString(navigator.languages, dmmyyyyTimeOptions);
};

export const isToday = (dateStr: string) => {
  const date = Temporal.Instant.from(dateStr)
    .toZonedDateTimeISO(Temporal.Now.timeZoneId())
    .toPlainDate();

  return Temporal.PlainDate.compare(date, Temporal.Now.plainDateISO()) === 0;
};
